import dayjs from 'dayjs';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeadCell, TableHeadRow, TableRow, Spinner } from '@client/toolkit';
import { useLazyGetInvoicesByIdDocumentQuery, InvoiceStatus } from '@client/api';
import { toast } from 'react-hot-toast';
import { InvoiceOverviewItem } from './mock-data';
import { SortableTableHeadCell } from '../../../../reporting/components/sortable-table-head-cell';

export type SortDirection = 'asc' | 'desc';

interface InvoiceOverviewTableProps {
  data: InvoiceOverviewItem[];
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSort: (column: string, direction: SortDirection) => void;
}

const statusColors: Record<InvoiceStatus, string> = {
  Paid: 'text-green-400',
  Unpaid: 'text-yellow-400',
  Overdue: 'text-red-400',
  Credited: 'text-blue-400',
  CreditNote: 'text-purple-400',
  PartiallyPaid: 'text-orange-400',
};

const formatCurrency = (amount: number, currency = 'NOK'): string => {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('DD.MM.YYYY');
};

export function InvoiceOverviewTable({ data, sortColumn, sortDirection, onSort }: InvoiceOverviewTableProps) {
  const { t } = useTranslation('agreements');
  const [getInvoiceDocument] = useLazyGetInvoicesByIdDocumentQuery();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleOpenInvoice = useCallback((invoiceId: string) => {
    setDownloadingId(invoiceId);
    getInvoiceDocument({ id: invoiceId })
      .unwrap()
      .then((blobUrl) => {
        const link = document.createElement('a');
        link.href = blobUrl as string;
        link.download = invoiceId;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        toast.error('Kunne ikke laste ned faktura. Prøv igjen senere.', { id: 'global-error' });
      })
      .finally(() => {
        setDownloadingId(null);
      });
  }, [getInvoiceDocument]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      onSort(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(column, 'asc');
    }
  };

  return (
    <div className="px-4 pb-4">
      <Table className="w-full table-fixed" size="small">
        <TableHead>
          <TableHeadRow>
            <TableHeadCell className="w-[18%]">{t('reports.invoiceTable.tenant')}</TableHeadCell>
            <SortableTableHeadCell
              id="invoiceNumber"
              label={t('reports.invoiceTable.invoiceNumber')}
              className="w-[10%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <SortableTableHeadCell
              id="invoiceDate"
              label={t('reports.invoiceTable.invoiceDate')}
              className="w-[12%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <SortableTableHeadCell
              id="dueDate"
              label={t('reports.invoiceTable.dueDate')}
              className="w-[12%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <SortableTableHeadCell
              id="amount"
              label={t('reports.invoiceTable.amount')}
              className="w-[12%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <SortableTableHeadCell
              id="amountOutstanding"
              label={t('reports.invoiceTable.outstanding')}
              className="w-[12%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <SortableTableHeadCell
              id="status"
              label={t('reports.invoiceTable.status')}
              className="w-[11%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {data.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell className="w-[18%]">
                <span className="font-medium text-white">{invoice.tenantName}</span>
              </TableCell>
              <TableCell className="w-[10%]">
                {downloadingId === invoice.id ? (
                  <span className="inline-flex items-center gap-1 text-brand-primary">
                    <Spinner size="xs" />
                  </span>
                ) : (
                  <a
                    onClick={() => handleOpenInvoice(invoice.id)}
                    className="text-brand-primary underline cursor-pointer"
                  >
                    #{invoice.invoiceNumber}
                  </a>
                )}
              </TableCell>
              <TableCell className="w-[12%]">
                <span className="text-white">{invoice.invoiceDate ? formatDate(invoice.invoiceDate) : '-'}</span>
              </TableCell>
              <TableCell className="w-[12%]">
                <span className="text-white">{invoice.dueDate ? formatDate(invoice.dueDate) : '-'}</span>
              </TableCell>
              <TableCell className="w-[12%]">
                <span className="text-white">
                  {invoice.amount === 0 && invoice.amountOutstanding !== 0
                    ? 'N/A'
                    : formatCurrency(invoice.amount, invoice.currency)}
                </span>
              </TableCell>
              <TableCell className="w-[12%]">
                <span className={invoice.amountOutstanding > 0 ? 'text-yellow-400' : 'text-white'}>
                  {formatCurrency(invoice.amountOutstanding, invoice.currency)}
                </span>
              </TableCell>
              <TableCell className="w-[11%]">
                <span className={statusColors[invoice.status]}>{t(`reports.invoiceStatus.${invoice.status}`)}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
